import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AIAgent, Category } from '../../types';
import AgentCard from './components/AgentCard';
import { SearchIcon, StarIcon, ClockIcon } from '../../components/icons/IconWrapper';
import Sidebar from '../../components/sidebar/Sidebar';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchAIAgents,
  clearAIAgentError,
  createUserAgent,
  updateUserAgent,
  deleteUserAgent
} from '../../redux/ai/aiAgentSlice';
import { useLogout } from '../../hooks/useLogout';
import ErrorModal from '../../components/common/ErrorModal';
import { getPublicCategories } from '../../services/api/categoryService';
import { Button } from '../../components/ui/button';
import { Search, Plus, Users } from 'lucide-react';
import UserAgentFormModal from '../../components/agent/UserAgentFormModal';
import DeleteConfirmModal from '../../components/agent/DeleteConfirmModal';
import { CreateAgentRequest, UpdateAgentRequest } from '../../services/api/aiAgentService';
import {
  TaskyEmptyState,
  TaskyErrorState,
  TaskyInput,
  ResponsiveAppShell,
  TaskySpinner,
  TaskyTopBar,
} from '../../components/tasky';
import { Inbox, SearchX } from 'lucide-react';

const AgentListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Redux stateから情報を取得
  const { agents, isLoading, error } = useAppSelector(
    (state) => state.aiAgent
  );
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  
  // ローカルstate
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<'favorite' | 'usage'>('favorite');
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorDetails, setErrorDetails] = useState({ title: '', message: '' });
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);

  // Agent modal state
  const [showAgentModal, setShowAgentModal] = useState<boolean>(false);
  const [editingAgent, setEditingAgent] = useState<AIAgent | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deletingAgent, setDeletingAgent] = useState<AIAgent | undefined>(undefined);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  
  // カテゴリ一覧を取得
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await getPublicCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('カテゴリの取得に失敗しました:', error);
      }
    };

    fetchCategories();
  }, []);

  // 初期データの取得
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        // tokenManagerを使用してトークンを取得
        const tokenManager = await import('../../utils/tokenManager').then(
          module => module.default
        );
        const storedToken = tokenManager.getToken();
        
        console.log('AgentListPage: 認証状態チェック', { 
          isAuthenticated, 
          hasToken: !!storedToken,
          tokenLength: storedToken?.length
        });

        // 認証トークンがある場合はエージェント一覧を取得
        if (storedToken) {
          console.log('AgentListPage: エージェント一覧を取得します');
          dispatch(fetchAIAgents(false));
        } else {
          console.warn('AgentListPage: 認証トークンがないため、エージェント取得をスキップします');
        }
      } catch (error) {
        console.error('AgentListPage: トークン確認中にエラーが発生しました:', error);
      }
    };

    fetchAgents();
  }, [dispatch, isAuthenticated]);
  
  // エラーを監視してモーダル表示
  useEffect(() => {
    if (error) {
      setErrorDetails({
        title: 'エラーが発生しました',
        message: `API通信エラー: ${error}\n\n詳細情報はコンソールを確認してください。`
      });
      setShowErrorModal(true);
    }
  }, [error]);
  
  // エラーモーダルを閉じる
  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    dispatch(clearAIAgentError());
  };
  
  // カテゴリー変更ハンドラー
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };
  
  // 検索ハンドラー
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  
  // ソートハンドラー
  const handleSortChange = (sortType: 'favorite' | 'usage') => {
    setSortBy(sortType);
  };
  
  // エージェントカードクリックハンドラー
  const handleAgentClick = (agent: AIAgent) => {
    navigate(`/chat/new?agent=${agent.id}`);
  };

  // Create agent handler
  const handleCreateAgent = () => {
    setEditingAgent(undefined);
    setShowAgentModal(true);
  };

  // Edit agent handler
  const handleEditAgent = (agent: AIAgent, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAgent(agent);
    setShowAgentModal(true);
  };

  // Delete agent handler
  const handleDeleteAgent = (agent: AIAgent, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingAgent(agent);
    setShowDeleteModal(true);
  };

  // Agent form submit handler
  const handleAgentSubmit = async (agentData: CreateAgentRequest | UpdateAgentRequest) => {
    try {
      if (editingAgent) {
        // Update existing agent
        await dispatch(updateUserAgent({ agentId: editingAgent.id, agentData })).unwrap();
      } else {
        // Create new agent
        await dispatch(createUserAgent(agentData as CreateAgentRequest)).unwrap();
      }
      setShowAgentModal(false);
      setEditingAgent(undefined);
    } catch (error: any) {
      console.error('Failed to save agent:', error);
      throw error; // Re-throw to show error in modal
    }
  };

  // Confirm delete handler
  const handleConfirmDelete = async () => {
    if (!deletingAgent) return;

    try {
      setIsDeleting(true);
      await dispatch(deleteUserAgent(deletingAgent.id)).unwrap();
      setShowDeleteModal(false);
      setDeletingAgent(undefined);
    } catch (error: any) {
      console.error('Failed to delete agent:', error);
      setErrorDetails({
        title: 'エージェント削除エラー',
        message: `エージェントの削除に失敗しました: ${error?.message || '不明なエラー'}`,
      });
      setShowErrorModal(true);
    } finally {
      setIsDeleting(false);
    }
  };
  
  // カテゴリフィルタリング関数
  const filterByCategory = (agent: AIAgent): boolean => {
    if (selectedCategory === 'all') return true;
    
    // カテゴリIDで比較
    if (selectedCategory === 'uncategorized') {
      return !agent.category_id;
    }
    
    return agent.category_id?.toString() === selectedCategory;
  };
  
  // 検索フィルタリング関数
  const filterBySearch = (agent: AIAgent): boolean => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      agent.title.toLowerCase().includes(query) ||
      (agent.description?.toLowerCase().includes(query) || false)
    );
  };
  
  // フィルタリングとソートを適用したエージェントリスト
  const filteredAgents = agents
    .filter(agent => ![1, 2, 3].includes(agent.id)) // id=1,2,3を除外
    .filter(agent => agent.is_visible_in_sidebar !== false) // is_visible_in_sidebarがfalseのものを除外
    .filter(filterByCategory)
    .filter(filterBySearch)
    .sort((a, b) => {
      if (sortBy === 'favorite') {
        // お気に入り順 (お気に入りが先)
        if (a.is_favorited && !b.is_favorited) return -1;
        if (!a.is_favorited && b.is_favorited) return 1;
      }
      
      // 使用頻度順 (多い順)
      const aCount = a.usage_count || 0;
      const bCount = b.usage_count || 0;
      return bCount - aCount;
    });

  // 実際に使用されているカテゴリのみを表示用に抽出
  const usedCategoryIds = new Set(
    agents
      .filter(agent => ![1, 2, 3].includes(agent.id)) // id=1,2,3を除外
      .filter(agent => agent.is_visible_in_sidebar !== false) // is_visible_in_sidebarがfalseのものを除外
      .filter(agent => agent.category_id)
      .map(agent => agent.category_id!.toString())
  );

  // 表示用カテゴリリスト（「すべて」+ 使用されているカテゴリのみ）
  const displayCategories = [
    { id: 'all', name: 'すべて' },
    ...categories.filter(category => usedCategoryIds.has(category.id.toString())).map(category => ({
      id: category.id.toString(),
      name: category.name
    }))
  ];

  // 未分類のエージェントがある場合は「未分類」カテゴリを追加
  const hasUncategorizedAgents = agents
    .filter(agent => ![1, 2, 3].includes(agent.id)) // id=1,2,3を除外
    .filter(agent => agent.is_visible_in_sidebar !== false) // is_visible_in_sidebarがfalseのものを除外
    .some(agent => !agent.category_id);
  if (hasUncategorizedAgents) {
    displayCategories.push({ id: 'uncategorized', name: '未分類' });
  }
  
  // ログアウト処理
  const handleLogout = useLogout();

  const sidebarAgents = agents.filter(agent => ![1, 2, 3].includes(agent.id) && agent.is_visible_in_sidebar !== false);
  const sidebar = (
    <Sidebar
      onLogout={handleLogout}
      agents={sidebarAgents}
      onSelectAgent={(agentId) => handleAgentClick(agents.find(a => a.id === agentId) || agents[0])}
      onToggleCollapse={setSidebarCollapsed}
      collapsed={sidebarCollapsed}
    />
  );
  const mobileSidebar = (
    <Sidebar
      onLogout={handleLogout}
      agents={sidebarAgents}
      onSelectAgent={(agentId) => handleAgentClick(agents.find(a => a.id === agentId) || agents[0])}
    />
  );

  return (
    <ResponsiveAppShell sidebar={sidebar} mobileSidebar={mobileSidebar} topbarTitle="エージェント一覧">

      <div className="flex min-h-[calc(100dvh-56px)] flex-col bg-white lg:h-screen lg:min-h-0">
        <TaskyTopBar
          title="エージェント一覧"
          icon={<Users className="w-[22px] h-[22px]" />}
          titleAddon={<span className="text-sm text-gray-500 font-normal">（全{sidebarAgents.length}種）</span>}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        >
          {user?.organization?.enable_user_created_agents && (
            <Button
              onClick={handleCreateAgent}
              size="sm"
              className="bg-primary text-white hover:bg-primary-hover flex items-center gap-2"
              data-tutorial="agents-create"
            >
              <Plus className="w-4 h-4" />
              エージェント作成
            </Button>
          )}
        </TaskyTopBar>
        <div className="flex-1 bg-warm-bg overflow-y-auto overflow-x-hidden p-4 sm:p-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4" data-tutorial="agents-search-sort">
              <div className="relative w-full sm:max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none z-10" />
                <TaskyInput
                  type="text"
                  className="pl-10"
                  placeholder="エージェントを検索..."
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant={sortBy === 'favorite' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleSortChange('favorite')}
                  className={`transition-colors ${
                    sortBy === 'favorite'
                      ? 'bg-primary text-white hover:bg-primary-hover'
                      : 'bg-white border-warm-border text-gray-600 hover:text-gray-900 hover:border-primary'
                  }`}
                  aria-label="お気に入り順に並び替え"
                >
                  <StarIcon />
                </Button>
                <Button
                  variant={sortBy === 'usage' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleSortChange('usage')}
                  className={`transition-colors ${
                    sortBy === 'usage'
                      ? 'bg-primary text-white hover:bg-primary-hover'
                      : 'bg-white border-warm-border text-gray-600 hover:text-gray-900 hover:border-primary'
                  }`}
                  aria-label="使用頻度順に並び替え"
                >
                  <ClockIcon />
                </Button>
              </div>
            </div>

            <div className="-mx-4 mb-6 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0" data-tutorial="agents-categories">
              {displayCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`shrink-0 px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                    selectedCategory === category.id
                      ? "bg-primary text-white"
                      : "bg-white border border-warm-border text-gray-600 hover:text-dark hover:border-primary"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
            
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <TaskySpinner size="lg" />
                <p className="text-sm text-gray-600">読み込み中...</p>
              </div>
            ) : error ? (
              <TaskyErrorState
                title="エラーが発生しました"
                description={error}
                className="my-8"
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-tutorial="agents-grid">
                {filteredAgents.map((agent) => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    onClick={() => handleAgentClick(agent)}
                    onEdit={(e) => handleEditAgent(agent, e)}
                    onDelete={(e) => handleDeleteAgent(agent, e)}
                  />
                ))}
                
                {filteredAgents.length === 0 && agents.length === 0 && (
                  <div className="col-span-full">
                    <TaskyEmptyState
                      icon={<Inbox />}
                      title="エージェントが見つかりませんでした"
                      description="データの読み込みに失敗した可能性があります。"
                    />
                  </div>
                )}

                {filteredAgents.length === 0 && agents.length > 0 && (
                  <div className="col-span-full">
                    <TaskyEmptyState
                      icon={<SearchX />}
                      title="該当するエージェントが見つかりませんでした"
                      description="検索条件を変更してお試しください。"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* エラーモーダル */}
      <ErrorModal
        show={showErrorModal}
        title={errorDetails.title}
        message={errorDetails.message}
        onClose={handleCloseErrorModal}
      />

      {/* エージェント作成・編集モーダル */}
      <UserAgentFormModal
        isOpen={showAgentModal}
        onClose={() => {
          setShowAgentModal(false);
          setEditingAgent(undefined);
        }}
        agent={editingAgent}
        onSubmit={handleAgentSubmit}
      />

      {/* エージェント削除確認モーダル */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingAgent(undefined);
        }}
        onConfirm={handleConfirmDelete}
        agentName={deletingAgent?.title || ''}
        isDeleting={isDeleting}
      />
    </ResponsiveAppShell>
  );
};

export default AgentListPage;
